<script setup lang="ts">
import { computed, ref } from 'vue'
import { useTicketByIdQuery, useTicketByQRCodeQuery } from '@/features/tickets/api/queries'
import { useImage } from '@/shared/composables/image'
import TicketAssignmentInterface from '@/features/tickets/ui/TicketAssignmentInterface.vue'
import AvatarPlaceholder from '@/features/tickets/assets/AvatarPlaceholder.vue'
import { ModalWrapper } from '@/shared/ui/modals'
import { useUserQuery } from '@/enitites/user/api/queries'
import { mdiClose } from '@mdi/js'
import SelectedTicketAdmin from '@/features/tickets/ui/SelectedTicketAdmin.vue'
import PurchaseReceipt from '@/features/tickets/ui/PurchaseReceipt.vue'
import RefundTicketInterface from '@/features/tickets/ui/RefundTicketInterface.vue'
import ChangeTierInterface from '@/features/tickets/ui/ChangeTierInterface.vue'

const props = defineProps<{
  ticketId?: number
  qrCode?: string
  value: boolean
  eventId: number
}>()

const emit = defineEmits<{
  (e: 'close-modal'): void
}>()

const { getImageById } = useImage()

const computedTicketId = computed(() => {
  return props.value && props.ticketId ? props.ticketId : undefined
})
const computedQRCode = computed(() => {
  return props.value && props.qrCode ? props.qrCode : undefined
})

const { data: ticketById, isLoading: isLoadingById, isError: isErrorById } = useTicketByIdQuery(computedTicketId)
const { data: ticketByQR, isLoading: isLoadingByQR, isError: isErrorByQR } = useTicketByQRCodeQuery(computedQRCode)

const ticket = computed(() => {
  if (props.ticketId) {
    return ticketById.value
  }
  if (props.qrCode) {
    return ticketByQR.value
  }
  return undefined
})

const computedAssignedUserId = computed(() => {
  return props.value && ticket.value?.assignedTo?.userId ? ticket.value.assignedTo.userId : undefined
})

const { data: userDetails, isLoading: isLoadingUserDetails } = useUserQuery(computedAssignedUserId)

const isLoading = computed(() => {
  if (props.ticketId) {
    return isLoadingById.value
  }
  if (props.qrCode) {
    return isLoadingByQR.value
  }
  return false
})

const isError = computed(() => {
  if (props.ticketId) {
    return isErrorById.value
  }
  if (props.qrCode) {
    return isErrorByQR.value
  }
  return false
})

const closeModal = (value: boolean) => {
  if (!value) {
    emit('close-modal')
  }
}

const getAgeByBirthdate = (birthdate: string): number => {
  const today = new Date()
  const birthDate = new Date(birthdate)
  let age = today.getFullYear() - birthDate.getFullYear()
  const monthDifference = today.getMonth() - birthDate.getMonth()
  if (monthDifference < 0 || (monthDifference === 0 && today.getDate() < birthDate.getDate())) {
    age -= 1
  }
  return age
}

const isShowAssignmentInterface = ref(false)
const isShowSuccessAssignmentMessage = ref(false)
const isShowChangeTierInterface = ref(false)
const isShowRefundInterface = ref(false)
const isShowReceiptInterface = ref(false)
</script>

<template>
  <ModalWrapper
    :is-show="value"
    modal-width="900px"
    @update:isShow="closeModal"
  >
    <v-card class="admin-ticket-modal">
      <div class="modal-header">
        <template v-if="isLoading">
          <v-skeleton-loader
            type="image"
            height="100"
            width="100"
          />
          <div class="skeleton-info">
            <v-skeleton-loader type="heading" />
            <v-skeleton-loader
              type="text@2"
              width="200"
            />
          </div>
        </template>

        <template v-else-if="isError">
          <p class="error-message">Error loading ticket data</p>
        </template>

        <template v-else-if="ticket">
          <div class="user-info">
            <AvatarPlaceholder
              v-if="!ticket.assignedTo?.avatar"
              background-color="#DDDDDD"
              color="#333333"
              size="100"
            />
            <v-avatar
              v-else
              size="100"
            >
              <v-img :src="getImageById(ticket.assignedTo.avatar, 200, 200, 'avatar')" />
            </v-avatar>
            <div class="user-details">
              <h2 class="user-name">
                {{ ticket.assignedTo?.dancerName || ticket.assignedTo?.email || 'Unassigned' }}
              </h2>
              <p
                v-if="ticket.assignedTo?.email"
                class="ticket-info"
              >
                {{ ticket.assignedTo?.email }}
              </p>
              <v-skeleton-loader
                v-if="isLoadingUserDetails"
                type="text@2"
                width="200"
              />
              <p
                v-if="userDetails"
                class="ticket-info"
              >
                {{ userDetails.fullName
                }}{{ userDetails.birthday ? `, ${getAgeByBirthdate(userDetails.birthday)}` : '' }}
              </p>
              <p
                v-if="userDetails"
                class="ticket-info"
              >
                {{ userDetails.city }}{{ userDetails.state ? ` ${userDetails.state}` : ''
                }}{{ userDetails.country ? `, ${userDetails.country}` : '' }}
              </p>
            </div>
          </div>
        </template>
      </div>

      <div class="modal-content">
        <div
          v-if="ticket"
          class="ticket-section"
        >
          <template v-if="isShowAssignmentInterface">
            <TicketAssignmentInterface
              :ticket="ticket"
              @close="isShowAssignmentInterface = false"
              @successful-assignment="isShowSuccessAssignmentMessage = true"
            />
          </template>
          <template v-else-if="isShowChangeTierInterface">
            <ChangeTierInterface
              :current-ticket="ticket"
              :event-id="eventId"
              is-admin
              @close="isShowChangeTierInterface = false"
            />
          </template>
          <template v-else-if="isShowRefundInterface">
            <RefundTicketInterface
              :current-ticket="ticket"
              @close="isShowRefundInterface = false"
              @show-tier-change-interface="isShowChangeTierInterface = true"
            />
          </template>
          <template v-else-if="isShowReceiptInterface">
            <PurchaseReceipt
              :purchase-id="ticket.purchaseId"
              @close="isShowReceiptInterface = false"
            />
          </template>
          <template v-else>
            <SelectedTicketAdmin
              :ticket="ticket"
              @show-assignment-interface="isShowAssignmentInterface = true"
              @show-tier-change-interface="isShowChangeTierInterface = true"
              @show-refund-interface="isShowRefundInterface = true"
              @show-receipt-interface="isShowReceiptInterface = true"
            />
          </template>
        </div>
        <v-snackbar
          v-model="isShowSuccessAssignmentMessage"
          absolute
          class="success-assignment-snackbar"
          color="var(--color-green)"
          :elevation="0"
          height="52"
        >
          Email sent to assignee
          <template #action="{ attrs }">
            <v-btn
              v-bind="attrs"
              icon
              @click="isShowSuccessAssignmentMessage = false"
            >
              <v-icon>{{ mdiClose }}</v-icon>
            </v-btn>
          </template>
        </v-snackbar>
      </div>
    </v-card>
  </ModalWrapper>
</template>

<style scoped lang="scss">
@import '@/assets/style/mixins';

.admin-ticket-modal {
  p {
    margin: 0;
  }

  @media (max-width: 600px) {
    padding-top: 50px !important;
  }

  .modal-header {
    position: relative;
    display: flex;
    align-items: center;
    gap: 16px;
    padding: 20px 60px;

    &::after {
      content: '';
      position: absolute;
      bottom: -3px;
      left: 0;
      width: 100%;
      height: 3px;
      background-image: linear-gradient(to bottom, rgb(0 0 0 / 10%), transparent);
    }

    .skeleton-info {
      display: flex;
      flex-direction: column;
      gap: 8px;
    }

    .error-message {
      color: var(--error-red);
      font-size: 16px;
      margin: 0;
    }

    .user-info {
      display: flex;
      align-items: center;
      gap: 16px;
      width: 100%;

      .user-details {
        flex: 1;
        display: flex;
        flex-direction: column;
        gap: 4px;

        .user-name {
          font-weight: 500;
          font-size: 20px;
          line-height: 1.3;
          color: var(--color-dark-800);
        }

        .ticket-info {
          font-size: 14px;
          line-height: 20px;
          color: var(--color-grey-300);
        }
      }
    }
  }

  .modal-content {
    padding: 24px;
    max-height: 60vh;
    overflow-y: auto;

    .ticket-section {
      grid-area: selected;
      position: relative;
      padding: 20px;
      border-radius: 16px;
      background-color: var(--color-grey-100);
      border: 1px solid var(--color-grey-600);
      overflow-y: auto;
    }
  }
}
</style>
